import { useCallback, useMemo } from 'react';
import { useChatActions, useChatHostRuntime } from '@aimsos/chat';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

import { buildAskPayload } from '../utils/buildAskPayload.js';

/**
 * The ONE way this app hands work to the shell's Concierge (CT-5721).
 *
 * Ask, and every "Review draft", "Draft email", "Answer", "Reply" or "Hand to
 * Concierge" in later tickets (CT-5713, CT-5722, CT-5724), call
 * `openConcierge({ intent, entity, ... })`. Nothing else in the app reaches the
 * chat.
 *
 * `available` is true only when the host runtime the shell publishes through
 * `@aimsos/chat` has `openConcierge`. Today it only has `openChatWithAgent`,
 * which cannot carry the record, a draft or the starters — so `available` stays
 * false and callers of a draft or a reply hide their entry points instead of
 * faking the flow.
 * TODO(CT-5721 / platform): the shell adds `openConcierge(request)` to its runtime.
 *
 * Ask is the one exception: until then it opens the shell chat on its Concierge,
 * without the record. Clearing the selected agent first lets the shell's own
 * "default agent for a new chat" rule pick the Concierge, since this app has no
 * agent list to pick it from. Standalone there is no chat at all: Ask toasts.
 *
 * For `intent: 'ask'` the copy the panel shows (intro, starters, placeholder) is
 * this app's, built by `buildAskPayload` with the name the caller resolved
 * (`entity.title`) and passed along; the host only renders it.
 *
 * It subscribes its caller to the shell's chat actions, which change with every
 * message the chat streams: call it from the smallest component that owns the
 * button (`ProfileAskButton`), never from a page or a list.
 *
 * `openConcierge` resolves to `true` when the host opened the panel. If the host
 * cannot (no Concierge agent, no shell for an Ask), it toasts and resolves to
 * `false`.
 */
export function useConciergeHandoff() {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const { clearAgent } = useChatActions();
  const hostRuntime = useChatHostRuntime();
  const hostOpenConcierge = hostRuntime?.openConcierge;
  const hostOpenChatWithAgent = hostRuntime?.openChatWithAgent;
  const available = typeof hostOpenConcierge === 'function';

  const toastCannotOpen = useCallback(
    (message) =>
      toast.error({
        title: t('profile.ask.errorTitle'),
        message: message ?? t('profile.ask.errorMessage'),
      }),
    [t, toast],
  );

  const openConcierge = useCallback(
    async (request) => {
      const isAsk = request?.intent === 'ask';

      if (typeof hostOpenConcierge !== 'function') {
        if (!isAsk) return false;
        if (typeof hostOpenChatWithAgent !== 'function') {
          toastCannotOpen();
          return false;
        }
        try {
          clearAgent?.();
          hostOpenChatWithAgent(null);
          return true;
        } catch (err) {
          toastCannotOpen(err?.message);
          return false;
        }
      }

      const payload = isAsk ? buildAskPayload(request, t) : request;

      try {
        const opened = await hostOpenConcierge(payload);
        if (opened === false) throw new Error(t('profile.ask.errorMessage'));
        return true;
      } catch (err) {
        toastCannotOpen(err?.message);
        return false;
      }
    },
    [hostOpenConcierge, hostOpenChatWithAgent, clearAgent, t, toastCannotOpen],
  );

  return useMemo(() => ({ available, openConcierge }), [available, openConcierge]);
}

export default useConciergeHandoff;
